///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Validation for the persisted savedViews slice. Malformed views are
/// dropped rather than repaired: a view whose camera pose is corrupt is
/// useless, and the user can always re-save it.
///

import { Constants } from "@/constants"
import { wgs } from "@/earthbrowser/utils/geographic"
import type { CameraSavedState } from "@/types/CameraSavedState"
import type { SavedView, SavedViewsState } from "../savedViewsSlice"

function isFiniteNumber(value: unknown): value is number {
	return typeof value === "number" && isFinite(value)
}

function isISODateString(value: unknown): value is string {
	return typeof value === "string" && !isNaN(Date.parse(value))
}

/**
 * Validate a single saved view. Returns the (possibly clamped) view, or
 * null when it is malformed beyond repair and should be dropped.
 */
export function validateSavedView(view: unknown): SavedView | null {
	if (!view || typeof view !== "object") return null
	const v = view as Partial<SavedView>

	if (typeof v.id !== "string" || v.id.length === 0) return null
	if (typeof v.name !== "string" || v.name.trim().length === 0) return null

	const camera = v.camera as Partial<CameraSavedState> | undefined
	if (!camera || typeof camera !== "object") return null
	if (!isFiniteNumber(camera.radius) || !isFiniteNumber(camera.theta) ||
		!isFiniteNumber(camera.phi) || !isFiniteNumber(camera.tilt) ||
		!isFiniteNumber(camera.bearing)) {
		return null
	}

	// Clamp radius to the valid camera altitude range (km, same as live camera)
	const minRadius = Constants.cMinCameraAltitude + wgs.a
	const maxRadius = Constants.cMaxCameraAltitude + wgs.a
	const radius = Math.max(minRadius, Math.min(maxRadius, camera.radius))

	const createdAt = isISODateString(v.createdAt) ? v.createdAt : new Date().toISOString()
	const updatedAt = isISODateString(v.updatedAt) ? v.updatedAt : createdAt

	return {
		id: v.id,
		name: v.name.trim(),
		camera: {
			radius,
			theta: camera.theta,
			phi: camera.phi,
			tilt: camera.tilt,
			bearing: camera.bearing,
		},
		focusTargetId: typeof v.focusTargetId === "string" ? v.focusTargetId : undefined,
		layers: Array.isArray(v.layers) && v.layers.length > 0
			? v.layers.filter(l => typeof l === "string" && l.length > 0)
			: undefined,
		tags: Array.isArray(v.tags) ? v.tags.filter(t => typeof t === "string" && t.length > 0) : [],
		createdAt,
		updatedAt,
	}
}

/**
 * Validate the whole slice, dropping malformed views and tombstones.
 */
export function validateSavedViewsState(state: unknown): SavedViewsState {
	const fallback: SavedViewsState = { views: {}, deleted: {}, lastSyncedAt: null }
	if (!state || typeof state !== "object") return fallback
	const s = state as Partial<SavedViewsState>

	const views: Record<string, SavedView> = {}
	if (s.views && typeof s.views === "object") {
		for (const [id, view] of Object.entries(s.views)) {
			const validated = validateSavedView(view)
			if (validated?.id === id) {
				views[id] = validated
			}
		}
	}

	const deleted: Record<string, string> = {}
	if (s.deleted && typeof s.deleted === "object") {
		for (const [id, deletedAt] of Object.entries(s.deleted)) {
			if (typeof id === "string" && id.length > 0 && isISODateString(deletedAt)) {
				deleted[id] = deletedAt
			}
		}
	}

	return {
		views,
		deleted,
		lastSyncedAt: isISODateString(s.lastSyncedAt) ? s.lastSyncedAt : null,
	}
}
